import { IClinicNumber, IWorkingHours } from '@dataTypes/clinic.type'
import { WorkingHoursPanelProps } from './WorkingHoursPanel.component'
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useAppDispatch } from '@store/hooks'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import {
  amPmLabelToHms,
  clinicNumberHasWorkingHoursSetup,
  clinicWorkingHoursDataEqual,
  parseHmsToSec,
} from '@utils/phoneManagement.utils'
import {
  buildWorkingHoursPayload,
  clonePayloadForClinicNumberId,
  toWorkingHoursReplaceRows,
} from '@utils/clinic.util'
import { fullDaysOfWeek } from '@constants/Common/date.constant'
import { bulkUpdateClinicNumbersWorkingHoursAction } from '@store/actions/settings-communication.action'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'

type WorkingHoursValidationSnapshot = {
  invalidWeekdays: Set<string>
  lunchInvalid: boolean
  invalidOverrideIds: Set<string>
}

const defaultLunchBreak: IWorkingHours = {
  working_hours_id: '',
  day_of_week: 'lunch_break',
  entity_id: '',
  entity_type: 'clinic_number',
  start_time: '12:00:00',
  end_time: '13:00:00',
}

const DEFAULT_OPEN = '08:00:00'
const DEFAULT_CLOSE = '17:00:00'
const SAVE_WORKING_HOURS_LOADING_CONTAINER = 'settings-call-flow-editor'
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

const isCalendarDay = (day: string): boolean => fullDaysOfWeek.includes(day)

const timesFromSiblings = (
  prev: IWorkingHours[],
  exceptDay: string,
  same: boolean,
): { start: string; end: string } => {
  if (!same) {
    return { start: DEFAULT_OPEN, end: DEFAULT_CLOSE }
  }
  const other = prev.find(
    (wh) => isCalendarDay(wh.day_of_week) && wh.day_of_week !== exceptDay,
  )
  if (other) {
    return { start: other.start_time, end: other.end_time }
  }
  return { start: DEFAULT_OPEN, end: DEFAULT_CLOSE }
}

const startTimeBeforeEndTime = (start: string, end: string): boolean => {
  const a = parseHmsToSec(start)
  const b = parseHmsToSec(end)
  if (a === null || b === null) return false
  return a < b
}

const ymdTodayInTimezone = (timeZone: string): string => {
  return new Date().toLocaleDateString('en-CA', { timeZone })
}

const buildValidationSnapshot = (
  workingHours: IWorkingHours[] | undefined,
  lunch: IWorkingHours,
  overrides: IWorkingHours[],
  timezone: string,
): WorkingHoursValidationSnapshot | null => {
  const invalidWeekdays = new Set<string>()
  for (const d of fullDaysOfWeek) {
    const row = workingHours?.find((wh) => wh.day_of_week === d)
    if (row && !startTimeBeforeEndTime(row.start_time, row.end_time)) {
      invalidWeekdays.add(d)
    }
  }
  const lunchInvalid = !startTimeBeforeEndTime(lunch.start_time, lunch.end_time)
  const invalidOverrideIds = new Set<string>()
  const today = ymdTodayInTimezone(timezone || 'America/Los_Angeles')
  for (const o of overrides) {
    const dateStr = o.day_of_week?.trim() ?? ''
    if (!dateStr || !ISO_DATE_RE.test(dateStr)) continue
    if (dateStr < today && o.working_hours_id) {
      invalidOverrideIds.add(o.working_hours_id)
    }
  }
  if (
    invalidWeekdays.size === 0 &&
    !lunchInvalid &&
    invalidOverrideIds.size === 0
  ) {
    return null
  }

  return { invalidWeekdays, lunchInvalid, invalidOverrideIds }
}

interface IWorkingHoursHooks {
  addOverride: () => void
  applyToAllNumbers: boolean
  currentIndex: number
  discardUnsavedChanges: () => void
  handleCancelOrClose: () => void
  handleNext: () => void
  hasWorkingHoursChangesToSave: boolean
  lunch: IWorkingHours
  onHideUnsavedCloseModal: () => void
  onSave: () => void
  overrides: IWorkingHours[]
  removeOverride: (id: string) => void
  setApplyToAllNumbers: Dispatch<SetStateAction<boolean>>
  showUnsavedCloseModal: boolean
  showNext: boolean
  timezone: string
  updateDay: (
    day_of_week: string,
    data: Partial<{ on: boolean; start: string; end: string }>,
  ) => void
  updateLunch: (key: string, value: string) => void
  updateOverride: (id: string, data: Partial<IWorkingHours>) => void
  updateTimezone: (tz: string) => void
  validationSnapshot: WorkingHoursValidationSnapshot | null
}

const useWorkingHours = (props: WorkingHoursPanelProps): IWorkingHoursHooks => {
  const {
    activeNumber,
    selectedNumbers,
    clinicNumbers,
    updateActiveNumber,
    onClinicNumbersSaved,
    onNext,
    onCancel,
  } = props

  const dispatch = useAppDispatch()
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const [lunch, setLunch] = useState<IWorkingHours>(
    activeNumber.workingHours?.find((wh) => wh.day_of_week === 'lunch_break') ??
      defaultLunchBreak,
  )
  const [overrides, setOverrides] = useState<IWorkingHours[]>([])
  const [showUnsavedCloseModal, setShowUnsavedCloseModal] = useState(false)
  const allNumbersHaveWorkingHours = useMemo(
    () =>
      selectedNumbers.length > 0 &&
      selectedNumbers.every((n) => clinicNumberHasWorkingHoursSetup(n)),
    [selectedNumbers],
  )
  const [applyToAllNumbers, setApplyToAllNumbers] = useState<boolean>(
    allNumbersHaveWorkingHours,
  )
  const [timezone, setTimezone] = useState<string>(
    activeNumber.workingHours?.[0]?.timezone ?? 'America/Los_Angeles',
  )
  const [validationSnapshot, setValidationSnapshot] =
    useState<WorkingHoursValidationSnapshot | null>(null)

  const currentIndex = useMemo(
    () =>
      activeNumber.clinic_number_id
        ? selectedNumbers.findIndex(
            (n) => n.clinic_number_id === activeNumber.clinic_number_id,
          )
        : -1,
    [selectedNumbers, activeNumber],
  )

  const showNext =
    !applyToAllNumbers &&
    currentIndex >= 0 &&
    currentIndex < selectedNumbers.length - 1

  const hasWorkingHoursChangesToSave = useMemo(() => {
    if (selectedNumbers.length === 0) return false
    const tz = timezone || 'America/Los_Angeles'

    if (applyToAllNumbers) {
      const base = buildWorkingHoursPayload(activeNumber, lunch, overrides, tz)
      return selectedNumbers.some((n) => {
        if (!n.clinic_number_id) return true
        const baseline = clinicNumbers.find(
          (c) => c.clinic_number_id === n.clinic_number_id,
        )
        if (!baseline) return true
        const wh = clonePayloadForClinicNumberId(base, n.clinic_number_id, tz)
        const effective: IClinicNumber = {
          ...n,
          same_working_hours: activeNumber.same_working_hours,
          apply_working_hours: activeNumber.apply_working_hours,
          workingHours: wh,
        }
        return !clinicWorkingHoursDataEqual(effective, baseline)
      })
    }

    return selectedNumbers.some((n) => {
      if (!n.clinic_number_id) return true
      const baseline = clinicNumbers.find(
        (c) => c.clinic_number_id === n.clinic_number_id,
      )
      if (!baseline) return true
      const effective: IClinicNumber =
        n.clinic_number_id === activeNumber.clinic_number_id
          ? {
              ...n,
              ...activeNumber,
              workingHours: buildWorkingHoursPayload(
                activeNumber,
                lunch,
                overrides,
                tz,
              ),
            }
          : n
      return !clinicWorkingHoursDataEqual(effective, baseline)
    })
  }, [
    selectedNumbers,
    clinicNumbers,
    activeNumber,
    lunch,
    overrides,
    timezone,
    applyToAllNumbers,
  ])

  const updateDay = useCallback(
    (
      day_of_week: string,
      data: Partial<{ on: boolean; start: string; end: string }>,
    ) => {
      const prev = activeNumber.workingHours ?? []
      const entityId = activeNumber.clinic_number_id ?? ''
      const sharedTz = prev.find((w) => w.timezone)?.timezone
      const same = !!activeNumber.same_working_hours

      if (data.on === false) {
        updateActiveNumber({
          workingHours: prev.filter((wh) => wh.day_of_week !== day_of_week),
        })
        return
      }

      if (data.on === true) {
        const i = prev.findIndex((wh) => wh.day_of_week === day_of_week)
        const { start, end } = timesFromSiblings(prev, day_of_week, same)
        if (i >= 0) {
          const next = [...prev]
          next[i] = {
            ...next[i],
            start_time: start,
            end_time: end,
          }
          updateActiveNumber({ workingHours: next })
        } else {
          const newRow: IWorkingHours = {
            entity_type: 'clinic_number',
            entity_id: entityId,
            day_of_week,
            start_time: start,
            end_time: end,
            ...(sharedTz ? { timezone: sharedTz } : {}),
          }
          updateActiveNumber({ workingHours: [...prev, newRow] })
        }
        return
      }

      if (!prev.some((wh) => wh.day_of_week === day_of_week)) {
        return
      }

      if (same) {
        const workingHours = prev.map((wh) => {
          if (!isCalendarDay(wh.day_of_week)) return wh
          const o = { ...wh }
          if (data.start !== undefined) o.start_time = data.start
          if (data.end !== undefined) o.end_time = data.end
          return o
        })
        updateActiveNumber({ workingHours })
        return
      }

      const workingHours = prev.map((wh) => {
        if (wh.day_of_week !== day_of_week) return wh
        const o = { ...wh }
        if (data.start !== undefined) o.start_time = data.start
        if (data.end !== undefined) o.end_time = data.end
        return o
      })
      updateActiveNumber({ workingHours })
    },
    [activeNumber, updateActiveNumber],
  )

  const updateTimezone = useCallback(
    (tz: string) => {
      setTimezone(tz)
      updateActiveNumber({ timezone: tz })
    },
    [updateActiveNumber],
  )

  const updateOverride = useCallback(
    (id: string, data: Partial<IWorkingHours>) => {
      setOverrides(
        overrides.map((o) =>
          o.working_hours_id === id ? { ...o, ...data } : o,
        ),
      )
    },
    [overrides],
  )

  const addOverride = useCallback(() => {
    const lastWH = overrides[overrides.length - 1]

    if (
      lastWH?.day_of_week.trim() === '' ||
      lastWH?.name === '' ||
      lastWH?.start_time === '' ||
      lastWH?.end_time === ''
    )
      return
    setOverrides([
      ...overrides,
      {
        working_hours_id: `MOCK_ID-${overrides.length}`,
        day_of_week: '',
        entity_id: activeNumber.clinic_number_id!,
        entity_type: 'clinic_number',
        start_time: '',
        end_time: '',
      },
    ])
  }, [overrides, activeNumber.clinic_number_id])

  const removeOverride = useCallback((id: string) => {
    setOverrides((prev) => prev.filter((o) => o.working_hours_id !== id))
  }, [])

  const updateLunch = useCallback((key: string, value: string) => {
    setLunch((prev) => ({ ...prev, [key]: amPmLabelToHms(value) }))
  }, [])

  const handleNext = useCallback(() => {
    if (!activeNumber.clinic_number_id) return
    const invalid = buildValidationSnapshot(
      activeNumber.workingHours,
      lunch,
      overrides,
      timezone,
    )
    if (invalid) {
      setValidationSnapshot(invalid)
      return
    }
    setValidationSnapshot(null)
    const workingHours = buildWorkingHoursPayload(
      activeNumber,
      lunch,
      overrides,
      timezone,
    )
    updateActiveNumber({ workingHours })
    onNext()
  }, [activeNumber, lunch, overrides, timezone, updateActiveNumber, onNext])

  const handleCancelOrClose = (): void => {
    if (hasWorkingHoursChangesToSave) {
      setShowUnsavedCloseModal(true)
      return
    }
    onCancel()
  }

  const discardUnsavedChanges = (): void => {
    setShowUnsavedCloseModal(false)
    onCancel()
  }

  const onHideUnsavedCloseModal = (): void => {
    setShowUnsavedCloseModal(false)
  }

  const onSave = useCallback(async () => {
    if (!activeNumber.clinic_number_id) return
    const invalid = buildValidationSnapshot(
      activeNumber.workingHours,
      lunch,
      overrides,
      timezone,
    )
    if (invalid) {
      setValidationSnapshot(invalid)
      return
    }
    setValidationSnapshot(null)
    showLoading({ container: SAVE_WORKING_HOURS_LOADING_CONTAINER })
    try {
      if (applyToAllNumbers) {
        const base = buildWorkingHoursPayload(
          activeNumber,
          lunch,
          overrides,
          timezone,
        )
        const items = selectedNumbers.map((n) => {
          const id = n.clinic_number_id!
          const wh = clonePayloadForClinicNumberId(base, id, timezone)
          return {
            clinic_number_id: id,
            same_working_hours: activeNumber.same_working_hours,
            apply_working_hours: activeNumber.apply_working_hours,
            working_hours: toWorkingHoursReplaceRows(wh),
          }
        })
        const updated = await dispatch(
          bulkUpdateClinicNumbersWorkingHoursAction(items),
        ).unwrap()
        onClinicNumbersSaved?.(updated)
        showToast(createSuccessToast('Saved', 'Working hours updated.'))
        onCancel()
        return
      }

      const items = selectedNumbers.map((n) => {
        const isActive = n.clinic_number_id === activeNumber.clinic_number_id
        const wh = isActive
          ? buildWorkingHoursPayload(activeNumber, lunch, overrides, timezone)
          : (n.workingHours ?? [])
        return {
          clinic_number_id: n.clinic_number_id!,
          same_working_hours: n.same_working_hours,
          apply_working_hours: n.apply_working_hours,
          working_hours: toWorkingHoursReplaceRows(wh),
        }
      })
      const updated = await dispatch(
        bulkUpdateClinicNumbersWorkingHoursAction(items),
      ).unwrap()
      onClinicNumbersSaved?.(updated)
      showToast(createSuccessToast('Saved', 'Working hours updated.'))
      onCancel()
    } catch {
      showToast(createFailedToast('Error', 'Could not save working hours.'))
    } finally {
      hideLoading(SAVE_WORKING_HOURS_LOADING_CONTAINER)
    }
  }, [
    showToast,
    showLoading,
    hideLoading,
    activeNumber,
    lunch,
    overrides,
    timezone,
    applyToAllNumbers,
    selectedNumbers,
    dispatch,
    onClinicNumbersSaved,
    onCancel,
  ])

  useEffect(() => {
    setValidationSnapshot(null)
  }, [activeNumber.workingHours, lunch, overrides, timezone])

  useEffect(() => {
    const newlunch =
      activeNumber.workingHours?.find(
        (wh) => wh.day_of_week === 'lunch_break',
      ) ?? defaultLunchBreak
    setLunch(newlunch)
    const newOverrides =
      activeNumber.workingHours?.filter(
        (wh) => ![...fullDaysOfWeek, 'lunch_break'].includes(wh.day_of_week),
      ) ?? []
    setOverrides(newOverrides)
  }, [activeNumber.workingHours])

  return {
    addOverride,
    applyToAllNumbers,
    currentIndex,
    discardUnsavedChanges,
    handleCancelOrClose,
    handleNext,
    hasWorkingHoursChangesToSave,
    lunch,
    onHideUnsavedCloseModal,
    onSave,
    overrides,
    removeOverride,
    setApplyToAllNumbers,
    showUnsavedCloseModal,
    showNext,
    timezone,
    updateDay,
    updateLunch,
    updateOverride,
    updateTimezone,
    validationSnapshot,
  }
}

export default useWorkingHours
